import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { CheckCircle, PencilSimple, CaretLeft, CircleNotch } from "@phosphor-icons/react";
import Skeleton from 'react-loading-skeleton';
import { CircleLoader } from "react-awesome-loaders";
import { Button, Accordion, Checkbox, Form, notify, Dropdown, Question } from "app/components";
import useLoginModal from "../../login/useLoginModal";
import { formatDate } from "../../../utils/formatDate";
import { cancelBookingSession, completeBookingSession, createBookings, createBookingSession } from "../api";
import { checkIsPerTicket, validateIndividualConfig } from "./utils";
import { INDIAN_CITIES } from '../../event-create/constants/data.js'
import { indianStates } from '../../event-create/components/step-4/indianStates.js';
import 'react-loading-skeleton/dist/skeleton.css';
import "./style";
import PriceBreakup from "./priceBreakup";

const CheckoutPc = () => {
  const { activeRequests, userData, paymentInstance } = useSelector((state) => state.app);
  const { primaryEventData, selectedCategory, attendeesDetails, selectedStateData, bookingData: bookedData, temporaryBookingState, priceBreakup } = useSelector(
    (state) => state.primaryBuyerFlow
  );
  const { get_to_know_details, image } = primaryEventData || {};
  const totalAttendees = checkIsPerTicket(get_to_know_details) ? selectedCategory?.count : 1;
  const [checked, setChecked] = useState(false);
  const [activeAttendee, setActiveAttendee] = useState(1); // First form open by default
  const [attendeesData, setAttendeesData] = useState(
   attendeesDetails?.length
     ? attendeesDetails
     : Array(totalAttendees)
         .fill(null)
         .map(() => ({}))
 );
 
  const [savedAttendees, setSavedAttendees ] = useState(
    Array(totalAttendees).fill(false)
  );

  const [allFormsSaved, setAllFormsSaved] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loginModal = useLoginModal();

  const isBookingInProgress = activeRequests?.includes('book-tickets');
  const isPriceLoading = activeRequests?.includes('get-price-breakup');




  useEffect(() => () => {
    if (paymentInstance) {
      paymentInstance.close();
    }
  }, []);

  const cancelSession = useCallback(() => {
    if (!bookedData && temporaryBookingState?.id) {
      cancelBookingSession(temporaryBookingState?.id);
    }
  }, [bookedData, temporaryBookingState]);

  useEffect(() => {
    window.addEventListener('beforeunload', cancelSession);

    return () => {
      window.removeEventListener('beforeunload', cancelSession);
    }
  }, [cancelSession]);

  function getStateByCity(cityName) {
    const city =  INDIAN_CITIES.find(c => c.label.toLowerCase() === cityName.toLowerCase());
    return city ? city.State : "City not found";
  }

  const city = JSON.parse(localStorage.getItem('selectedCity') || '{}').label;
  const [selectedState, setSelectedState] = useState(selectedStateData ?? getStateByCity(city));

  const handleStateChange = (id) => {
    const selectedStateValue = indianStates.find((state) => state.id === id);
    setSelectedState(selectedStateValue.label);
    setAttendeesData((prevData) => {
      const updatedData = [...prevData];
      updatedData[activeAttendee - 1] = {
        ...updatedData[activeAttendee - 1],
        state: selectedStateValue,
      };
      return updatedData; 
    });
    dispatch({ type: "SELECTED_STATE_DATA:SET", data: selectedState });
  };
  const getTotalCost = () => {
    return priceBreakup?.items?.[0]?.final_amount;
  };

  const initiatePayment = async (bookingData, instance = paymentInstance) => {
    if (!instance) {
      console.error("Zoho Payments instance not initialized.");
      return;
    }

    try {
      const options = {
        amount: String(getTotalCost()),
        currency_code: "INR",
        payments_session_id: bookingData?.booking?.pg_order_id,
        reference_number: String(primaryEventData?.smp_id),
        currency_symbol: "₹",
        business: "Dankbarkeit Entertainment LLP",
        description: "",
        address: {
          name: userData?.name,
          email: userData?.email,
          phone: attendeesDetails?.[0]?.phone_number || userData?.mobile,
        },
      };

      const data = await instance.requestPaymentMethod(options);
      console.log(data);
      completeBookingSession(
        bookingData?.booking?.id,
        {
        amount: getTotalCost(),
        invoice_id: priceBreakup?.id,
        status: "SUCCESS",
        smp_sale_id: bookingData?.booking?.id,
        payment_id: data?.payment_id,
        order_id: bookingData?.booking?.pg_order_id,
        pay_signature: "",
      }, (successData) => {
            window.fbq("track", "Purchase", {
            value: successData?.total_price,
            currency: "INR",
            content_ids: [successData?.event_details?.id],
            content_name: successData?.event_name,
            content_type: "ticket",
            num_items: successData?.tickets_booked
          });
        notify('success', "Payment Successful! Booking your tickets...");
        navigate("/event/booking/success", {
          state: {
            bookingData: successData,
            primaryEventData,
            selectedCategory,
            attendeesDetails,
          },
          replace: true,
        });
        dispatch({ type: "CLEAR_DATA" });
      }, () => {
        navigate(`/event/${primaryEventData?.id}`, { replace: true });
      });
    } catch (err) {
      if (err.code !== "widget_closed") {
        console.error("Payment Error:", err);
        notify('error', "Payment Failed!");
      } else notify('error', "Failed to complete payment!");
      cancelBookingSession(bookingData?.booking?.id);
      navigate(`/event/${primaryEventData?.id}`, { replace: true });
    } finally {
      await instance.close();
      dispatch({ type: "CLEAR_DATA" });
    }
  };

  const handleBuyTickets = () => {
    if (!userData?.email) {
      loginModal.onOpen();
    } else {
      if (selectedCategory?.totalPrice > 0) {
        const config = {
          account_id: "60032703179",
          domain: "IN",
          otherOptions: {
            api_key: "1003.7ea8cbf8cfac3e5960a8b76159a5f6df.be33a0230584d5fa3cf105f75df9b4b5",
          },
        };
        try {
          const bookings = {
            event_id: primaryEventData?.id,
            total_price: getTotalCost(),
            invoice_id: priceBreakup?.id,
            total_ticket_count: selectedCategory?.count,
            ticket_list: [
              {
                ticket_slap_id: selectedCategory?.id,
                count: selectedCategory?.count,
              },
            ],
            get_to_know_details: {
              details: attendeesDetails,
              state: selectedState,
            },
          };
          createBookingSession(bookings, (data) => {
            let instance;
            if (!paymentInstance) {
              instance = new window.ZPayments(config);
              dispatch({ type: 'PAYMENT:INSTANCE:SET', data: instance });
            }
            initiatePayment(data, instance);
          }, () => notify('error', 'Failed to initiate payment!'));
        } catch (e) {
          console.log(e);
          notify('error', 'Failed to initiate payment!');
        }
      } else {
        const bookings = {
          event_id: primaryEventData?.id,
          total_price: 0,
          total_ticket_count: selectedCategory?.count,
          ticket_list: [
            {
              ticket_slap_id: selectedCategory?.id,
              count: selectedCategory?.count,
            },
          ],
          get_to_know_details: {
            details: attendeesDetails,
            state: selectedState
          }
        };
        createBookings(bookings,(data)=>{
          navigate("/event/booking/success", {
            state: {
              bookingData: data?.booking,
              primaryEventData,
              selectedCategory,
              attendeesDetails,
            },
            replace: true,
          });
          dispatch({ type: "CLEAR_DATA" });
        }, () => {
          navigate(`/event/${primaryEventData?.id}`, { replace: true });
          dispatch({ type: "CLEAR_DATA" });
        });
      }
    }
  };

  const handleToggle = (attendeeNumber) => {
    if (activeAttendee !== attendeeNumber) {
      setActiveAttendee(
        attendeeNumber === activeAttendee ? null : attendeeNumber
      );
    }
  };

  const validateForm = (formConfig) => {
    const activeAttendeeValue = attendeesData[activeAttendee - 1];
    let err = {};
    formConfig?.forEach((config) => {
      if ((config?.specificTicketType && config?.ticketTypes?.length && config?.ticketTypes?.includes(selectedCategory?.ref_id)) || !config?.specificTicketType) {
        const error = validateIndividualConfig(config, activeAttendeeValue, userData);
        err = { ...err, ...error };

        if (config?.conditions?.length) {
          config.conditions.forEach((condition) => {
            if (activeAttendeeValue?.[config?.id]?.includes(condition?.choice)) {
              const conditionErr = validateIndividualConfig([condition?.config], activeAttendeeValue, userData);
              err = { ...err, ...conditionErr };
            }
          });
        }
      }
    });

    return err;
  };

  // Save the attendee data and move to the next attendee form
  const handleSave = (attendeeNumber) => {
    console.log(`Attendee ${attendeeNumber} details saved.`);
    const error = validateForm(get_to_know_details);
    if (Object.keys(error).length) {
      setErrors(error);
    } else {
      setErrors({});
      const newSavedAttendees = [...savedAttendees];
      newSavedAttendees[attendeeNumber - 1] = true;
      setSavedAttendees(newSavedAttendees);
  
      const allSaved = newSavedAttendees.every((saved) => saved);
      setAllFormsSaved(allSaved); // Track if all forms are saved
      dispatch({ type: "ATTENDEES_DETAILS:SET", data: attendeesData });
  
      if (attendeeNumber < totalAttendees && !allSaved) {
        setActiveAttendee(attendeeNumber + 1);
      } else {
        handleToggle(attendeeNumber);
        setActiveAttendee(null);
      }
    }
  };

  const handleFormChange = (newValue, config, wholeConfig) => {
    if (wholeConfig) {
      const updatedData = [...attendeesData];
      updatedData[activeAttendee - 1] = {
        ...updatedData[activeAttendee - 1],
        ...wholeConfig,
      };

      dispatch({ type: "ATTENDEES_DETAILS:SET", data: updatedData });
      setAttendeesData(updatedData);
      return;
    }
    const updatedData = [...attendeesData];
    updatedData[activeAttendee - 1] = {
      ...updatedData[activeAttendee - 1],
      [config.id]: newValue,
    };
    dispatch({ type: "ATTENDEES_DETAILS:SET", data: updatedData });
    setAttendeesData(updatedData);
  };

  useEffect(() => {
    dispatch({ type: "SELECTED_STATE_DATA:SET", data: selectedState });
    }, [selectedState]);
  
  useEffect(() => {
    window.scrollTo(0, 0);

    if (Object.keys(primaryEventData || {}).length === 0) navigate('/', { replace: true });
  }, []);

  useEffect(() => {
  if (window.fbq && primaryEventData && selectedCategory) {
    window.fbq("track", "InitiateCheckout", {
      content_name: primaryEventData?.name,
      content_ids: [primaryEventData.id],
      content_type: "event",
      num_items: selectedCategory.count
    });
  }
}, []);


  if (activeRequests?.includes('book-tickets-completion')) {
    return (
      <div className="flex flex-col flex-1 justify-center items-center">
        <CircleLoader
          meshColor="#FF2459"
          lightColor="#E0E7FF"
          duration={2.5}
          desktopSize="90px"
        />
        <p className="pt-4 heading-24 text-black text-center">Just a moment — your tickets are on their way 😬</p>
      </div>
    );
  }

  return (
    <div className="mt-[70px] h-full md:mt-[100px] p-6 relative md:flex md:justify-center md:gap-[40px] md:px-[24px] md:py-7 md:bg-grey-5">
      <div className="w-[600px] bg-grey-5 flex flex-col gap-3">
        <div
          className="flex items-center gap-4 cursor-pointer"
          onClick={() => navigate(-1)}
        >
          <div className="p-[7px] sha-1 w-fit">
            <CaretLeft size={20.571} />
          </div>
          <p>Back</p>
        </div>
        {Array.from({ length: totalAttendees }, (_, i) => (
          <div
            key={i + 1}
            className={`flex flex-col gap-6 bg-white px-8 ${activeAttendee === i + 1 ? "py-8" : "py-6"} md:rounded-[20px] sha-2 h-fit`}
            onClick={() =>
              savedAttendees[i] && allFormsSaved ? handleToggle(i + 1) : null
            } // Allow click only if all forms are saved
          >
            <div className="flex justify-between items-center cursor-pointer">
              <h1 className="heading-16">{i === 0 ? 'Primary Booker' : `Attendee ${i+1} details`}</h1>
              <div className="flex gap-6 items-center">
                {savedAttendees[i] && activeAttendee !== i + 1 && (
                  <>
                    <CheckCircle size={24} color="#009D64" weight="fill" />
                    <PencilSimple
                      size={20}
                      color="#878787"
                      onClick={() => handleToggle(i + 1)} // Open form for editing
                    />
                  </>
                )}
              </div>
            </div>

            {activeAttendee === i + 1 && (
              <>
                <Form
                  formConfig={get_to_know_details}
                  value={attendeesData[i]} // Pass the current attendee's data
                  onFormChange={handleFormChange}
                  errors={errors}
                  setErrors={setErrors}
                  disabled={i===0}
                  attendeesData={attendeesData}
                  ticketCategory={selectedCategory}
                />
                <Button
                  type="primary-black"
                  className="self-end save-btn"
                  onClick={() => {handleSave(i + 1);}} 
                >
                  Done
                </Button>
              </>
            )}
          </div>
        ))}
      </div>

      <div className="mt-[48px] hidden md:flex flex-col gap-4 p-8 w-[472px] h-fit bg-white rounded-[20px] sha-2">
        <div className="flex flex-col gap-10 h-auto">
          <div className="flex gap-5 items-start pt-3">
            <div className="flex flex-col gap-2 items-start">
              <h1 className="heading-16">{primaryEventData?.name}</h1>
              <p className="label-14 text-grey-70">
                {formatDate(primaryEventData?.start_date)}
              </p>
              <span className="px-3 py-1 bg-light rounded-2xl label-12 text-primary">
                {selectedCategory?.count} x {selectedCategory?.name}
              </span>
            </div>
            <img
              src={image}
              alt="event-image"
              className="w-[178px] h-[102px] rounded-xl object-cover"
            />
          </div>

          <hr />
          <Accordion
            initialState={true}
            content={
              <PriceBreakup
              ticketPrice={1000}
              isGstRegistered={true}
              isFeeOwnedByUser={true}
              eventId={primaryEventData?.smp_id}
              ticketId={selectedCategory?.id}
              state={selectedState}
              count={selectedCategory?.count}
              isPaid={selectedCategory?.is_paid}
            />
            }
            title="Payment details"
            className="payment-details"
          />
          <Dropdown
            label="State"
            width="100%"
            options={indianStates}
            value={selectedState}
            onChange={handleStateChange}
            hasSearch={true}
          />
          <div className="w-full mt-auto pt-10 flex flex-col gap-5 items-center border-t-[#FFF2F2] border-t border-solid bg-[#FDFDFD]">
            <Checkbox
              checked={checked}
              label={
                <p className="text-14 text-grey-70">
                  I acknowledge and accept the{" "}
                  <Link to="/terms-and-conditions" className="text-black underline">
                    Terms of service
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy-policy" className="text-black underline">
                    Privacy policy.
                  </Link>
                </p>
              }
              onChange={() => {setChecked(!checked);}}
            />
            {isPriceLoading ? (
              <Skeleton containerClassName="h-[60px] w-full" className="h-full rounded-xl" />
            ) : (
              <Button
              className="w-full"
              loading={isBookingInProgress}
              disabled={!checked || !savedAttendees.every((saved) => saved) || isBookingInProgress}
              onClick={handleBuyTickets}
            >
              {selectedCategory?.totalPrice > 0 ? "Pay Now" : "Confirm Booking"}
            </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPc;
